import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  CATEGORIES_JOURNAL,
  dateHeureTunis,
  libelleAction,
  lienEntite,
  type JournalEntree,
  type PageJournal,
  type UtilisateurPublic,
} from '@rh/shared';
import { appel } from '../api';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { NavAdministration } from '../composants/NavAdministration';

interface Filtres {
  categorie: string;
  utilisateur: string;
  du: string;
  au: string;
}

const VIDES: Filtres = { categorie: '', utilisateur: '', du: '', au: '' };

function requete(filtres: Filtres, avant?: string): string {
  const parametres = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres)) if (valeur) parametres.set(cle, valeur);
  if (avant) parametres.set('avant', avant);
  const texte = parametres.toString();
  return texte ? `/journal?${texte}` : '/journal';
}

const json = (valeur: unknown) => JSON.stringify(valeur, null, 2);

/** Journal d'audit, en lecture seule (docs/SECURITE_RGPD.md § Accès). */
export function AdminJournal() {
  const id = useId();
  const [comptes, setComptes] = useState<UtilisateurPublic[]>([]);
  const [saisie, setSaisie] = useState<Filtres>(VIDES);
  const [filtres, setFiltres] = useState<Filtres>(VIDES);
  const [entrees, setEntrees] = useState<JournalEntree[] | null>(null);
  const [suivant, setSuivant] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<UtilisateurPublic[]>('/utilisateurs')
      .then(setComptes)
      .catch(() => setComptes([]));
  }, []);

  /** Numéro de la dernière lecture lancée : une réponse plus ancienne (autres filtres) est ignorée. */
  const generation = useRef(0);

  const charger = useCallback(async (actifs: Filtres, avant?: string) => {
    const numero = ++generation.current;
    setChargement(true);
    setErreur(null);
    try {
      const page = await appel<PageJournal>(requete(actifs, avant));
      if (numero !== generation.current) return;
      setEntrees((deja) => (avant ? [...(deja ?? []), ...page.entrees] : page.entrees));
      setSuivant(page.suivant);
    } catch (e) {
      if (numero === generation.current) setErreur(e instanceof Error ? e.message : 'Lecture impossible');
    } finally {
      if (numero === generation.current) setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger(filtres);
  }, [charger, filtres]);

  function filtrer(evenement: FormEvent): void {
    evenement.preventDefault();
    setFiltres({ ...saisie });
  }

  const champ = (cle: keyof Filtres) => ({
    id: `${id}-${cle}`,
    value: saisie[cle],
    onChange: (e: { target: { value: string } }) => setSaisie((s) => ({ ...s, [cle]: e.target.value })),
  });

  return (
    <section>
      <NavAdministration />
      <div className="entete-annonce">
        <h2>Journal d’audit</h2>
      </div>
      <p className="mention">
        Qui a fait quoi et quand, du plus récent au plus ancien. Les entrées ne se modifient ni ne s’effacent ; le
        détail peut contenir des données de candidats.
      </p>

      <form className="filtres-journal" noValidate onSubmit={filtrer}>
        <div className="champ">
          <label htmlFor={`${id}-categorie`}>Catégorie</label>
          <select {...champ('categorie')}>
            <option value="">Toutes</option>
            {CATEGORIES_JOURNAL.map((c) => (
              <option key={c.cle} value={c.cle}>
                {c.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor={`${id}-utilisateur`}>Compte</label>
          <select {...champ('utilisateur')}>
            <option value="">Tous</option>
            {comptes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor={`${id}-du`}>Du</label>
          <input type="date" {...champ('du')} />
        </div>
        <div className="champ">
          <label htmlFor={`${id}-au`}>Au</label>
          <input type="date" {...champ('au')} />
        </div>
        <div className="boutons">
          <button type="submit" className="bouton" disabled={chargement}>
            Filtrer
          </button>
          <button
            type="button"
            className="bouton-second"
            disabled={chargement}
            onClick={() => {
              setSaisie(VIDES);
              setFiltres(VIDES);
            }}
          >
            Effacer les filtres
          </button>
        </div>
      </form>

      {erreur && <Erreur message={erreur} />}
      {!entrees && chargement && <Chargement quoi="du journal" />}
      {entrees && entrees.length === 0 && <Vide enfants="Aucune entrée pour ces filtres." />}
      {entrees && entrees.length > 0 && (
        <ul className="liste-admin bloc">
          {entrees.map((entree) => {
            const lien = lienEntite(entree.entite, entree.entiteId);
            return (
              <li key={entree.id} className="entree-journal">
                <div className="ligne-admin">
                  <time dateTime={entree.horodatage}>{dateHeureTunis(entree.horodatage)}</time>
                  <strong>{libelleAction(entree.action)}</strong>
                  <span>{entree.utilisateur ? entree.utilisateur.nom : 'Système'}</span>
                  {lien && (
                    <Link to={lien} className="lien-annonce">
                      Ouvrir
                    </Link>
                  )}
                </div>
                {(entree.avant !== null || entree.apres !== null) && (
                  <details>
                    <summary>Détail</summary>
                    {entree.avant !== null && (
                      <>
                        <p className="mention">Avant</p>
                        <pre className="detail-journal">{json(entree.avant)}</pre>
                      </>
                    )}
                    {entree.apres !== null && (
                      <>
                        <p className="mention">Après</p>
                        <pre className="detail-journal">{json(entree.apres)}</pre>
                      </>
                    )}
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {suivant && (
        <button type="button" className="bouton-second" disabled={chargement} onClick={() => void charger(filtres, suivant)}>
          {chargement ? 'Chargement…' : 'Afficher les entrées plus anciennes'}
        </button>
      )}
    </section>
  );
}
